<script>
    import { mapMutations, mapState } from "vuex";
    import { OverlayManager } from "../../overlay-manager.js";
    import ModalManager from "../modal-manager.js";
    export default {
        name: "ModalBase",
        props: {
            name: {
                type: String,
                required: true,
            },
            initData: {
                type: Object,
                default: () => ({}),
            },
            params: {
                type: Object,
                default: () => ({}),
            },
            promiseRes: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                id: null,
                options: {},
                pages: [],
                index: 0,
            };
        },
        mounted() {
            this.setModalData({ name: this.name, data: Object.assign({}, this.initData) });
            import(`../groups/${this.name}.json`).then((groupData) => {
                this.options = Object.assign({}, groupData);
                if (this.options.pages) {
                    this.$set(this, "pages", this.options.pages);
                }
            });
            this.id = OverlayManager.register(this);
        },
        methods: {
            setInfo(info) {
                if (this.pages.length > 0) {
                    this.$set(this.pages, this.index, {
                        ...this.pages[this.index],
                        ...info,
                    });
                } else {
                    this.$set(this, "options", { ...this.options, ...info });
                }
            },
            close(resolve) {
                if (resolve) {
                    this.promiseRes.resolve(this.data);
                } else {
                    this.promiseRes.reject();
                }
                this.setModalData({ name: this.name, data: {} });
                ModalManager.close(this.id);
            },
            tryClose() {
                if (
                    confirm("You have unsaved changes. Are you sure you want to close?")
                ) {
                    this.close(false);
                }
            },
            previous() {
                this.index--;
            },
            goto(index) {
                this.index = index;
            },
            next() {
                this.index++;
            },
            handleEscapePress() {
                this.$refs.page.close();
            },
            handleOverlayClick() { },
            ...mapMutations(["setModalData"]),
        },
        computed: {
            widthClass() {
                return this.options.size ?? "md";
            },
            currentComponent() {
                // Return page component; if no pages, return top level component.
                return (
                    this.pages?.[this.index]?.component ?? this.options.component
                );
            },
            data: {
                get() {
                    return this.modalData[this.name] ?? {};
                },
                set(value) {
                    this.setModalData({ name: this.name, data: value });
                },
            },
            leftButtonText() {
                let currentButtons;
                if (this.pages?.[this.index]?.buttons)
                    currentButtons = this.pages[this.index].buttons;
                else if (this.options.buttons) currentButtons = this.options.buttons;
                else currentButtons = null;
                switch (currentButtons) {
                    case "nav":
                    case "save":
                        return this.index > 0 ? "Back" : "Close";
                    case "close":
                        return "Close";
                    case "upload":
                        return "Cancel";
                    default:
                        return null;
                }
            },
            rightButtonText() {
                let currentButtons;
                if (this.pages?.[this.index]?.buttons)
                    currentButtons = this.pages[this.index].buttons;
                else if (this.options.buttons) currentButtons = this.options.buttons;
                else currentButtons = null;
                switch (currentButtons) {
                    case "nav":
                        return this.index < this.pages.length - 1 ? "Next" : "Finish";
                    case "save":
                        return "Save";
                    case "finish":
                        return "Finish";
                    case "upload":
                        return "Upload";
                    default:
                        return null;
                }
            },
            ...mapState(["modalData"]),
        },
        components: {
            GeneralDetails: () =>
                import(
        /* webpackChunkName: "ContactDetails" */ "../layouts/contact/GeneralDetails.vue"
                ),
            ROIDetails: () =>
                import(
        /* webpackChunkName: "ContactROI" */ "../layouts/contact/ROIDetails.vue"
                ),
            ContactDetails: () =>
                import(
        /* webpackChunkName: "ContactInformation" */ "../layouts/contact/ContactDetails.vue"
                ),
            InsuranceDetails: () =>
                import(
        /* webpackChunkName: "InsuranceDetails" */ "../layouts/insurance/InsuranceDetails.vue"
                ),
            PolicyHolder: () =>
                import(
        /* webpackChunkName: "PolicyHolder" */ "../layouts/insurance/PolicyHolder.vue"
                ),
            PolicyInfo: () =>
                import(
        /* webpackChunkName: "PolicyInfo" */ "../layouts/insurance/PolicyInfo.vue"
                ),
            AssessSelect: () =>
                import(
        /* webpackChunkName: "AssessSelect" */ "../layouts/assessment/AssessSelect.vue"
                ),
            AssessKey: () =>
                import(
        /* webpackChunkName: "AssessKey" */ "../layouts/assessment/AssessKey.vue"
                ),
            AssessReview: () =>
                import(
        /* webpackChunkName: "AssessMessage" */ "../layouts/assessment/AssessReview.vue"
                ),
            AssessSuccess: () =>
                import(
        /* webpackChunkName: "AssessSuccess" */ "../layouts/assessment/AssessSuccess.vue"
                ),
            ViewAnswers: () =>
                import(
        /* webpackChunkName: "ViewAnswers" */ "../layouts/assessment/ViewAnswers.vue"
                ),
            DiagnosisEditor: () =>
                import(
        /* webpackChunkName: "DiagnosisEditor" */ "../layouts/DiagnosisEditor.vue"
                ),
            AddAppointment: () =>
                import(
        /* webpackChunkName: "AddAppointment" */ "../layouts/AddAppointment.vue"
                ),
            AddCPT: () =>
                import(/* webpackChunkName: "AddCPT" */ "../layouts/AddCPT.vue"),
            ProviderRegister: () =>
                import(
        /* webpackChunkName: "ProviderRegister" */ "../layouts/ProviderRegister.vue"
                ),
            PatientRegister: () =>
                import(
        /* webpackChunkName: "PatientRegister" */ "../layouts/PatientRegister.vue"
                ),
            DocumentUpload: () =>
                import(
        /* webpackChunkName: "DocumentUpload" */ "../layouts/DocumentUpload.vue"
                ),
            InputModal: () =>
                import(/* webpackChunkName: "InputModal" */ "../layouts/InputModal.vue"),
            UnsetModal: () =>
                import(/* webpackChunkName: "UnsetModal" */ "../layouts/UnsetModal.vue"),
            BrowserInformation: () =>
                import(
        /* webpackChunkName: "BrowserInformation" */ "../layouts/BrowserInformation.vue"
                ),
            BrowserInformationKey: () =>
                import(
        /* webpackChunkName: "BrowserInformationKey" */ "../layouts/BrowserInformationKey.vue"
                ),
            OutcomeSelect: () =>
                import(
     /* webpackChunkName: "AssessSelect" */ "../layouts/assessment/OutcomeSelect.vue"
                ),

        },
    };
</script>
<template>
    <div class="mhi-modal-wrapper">
        <div class="mhi-modal-body" :class="widthClass">
            <div class="modal-header">
                <h5 class="modal-title">
                    {{
            options.pages?.[index]?.title ??
            options.title ??
            "Please set a title text."
                    }}
                </h5>
                <button type="button"
                        class="btn-close"
                        aria-label="Close"
                        @click="$refs.page.close()"></button>
            </div>
            <div class="modal-body">
                <component v-if="currentComponent != ''"
                           :is="currentComponent"
                           ref="page"
                           v-model="data"
                           :init="Object.keys(initData).length > 0"
                           :params="params"
                           @info="setInfo"
                           @close="close"
                           @unsafe="tryClose"
                           @previous="previous"
                           @goto="goto"
                           @next="next" />
                <div v-else>No or invalid component name provided.</div>
            </div>
            <div class="modal-footer">
                <component v-if="options.pages?.[index]?.footer ?? options.footer"
                           :is="options.pages?.[index]?.footer ?? options.footer"></component>
                <button v-if="leftButtonText"
                        type="button"
                        class="btn btn-white ms-auto"
                        @click="$refs.page.previous()">
                    {{ leftButtonText }}
                </button>
                <button v-if="rightButtonText"
                        type="button"
                        class="btn btn-primary"
                        @click="$refs.page.next()">
                    {{ rightButtonText }}
                </button>
            </div>
        </div>
    </div>
</template>
